export default function initializeMembershipDirectory() {
    document.querySelectorAll('.block-membership-directory').forEach(function (block) {
        new MembershipDirectory(block);
    });
}

class MembershipDirectory {
    constructor(block) {
        this.block = block;
        this.form = block.querySelector('[data-membership-filters]');
        this.list = block.querySelector('[data-membership-list]');
        this.loader = block.querySelector('[data-membership-loader]');
        this.pagination = block.querySelector('[data-membership-pagination]');
        this.columnClass = this.buildColumnClass(parseInt(block.dataset.columns) || 3);

        this.state = {
            search: '',
            sector: '',
            location: '',
            page: 1,
        };

        this.noImage = block.dataset.noimage || '';
        this.emptyMessage = block.dataset.emptyMessage || '';
        this.perPage = parseInt(block.dataset.perPage) || 9;
        this.totalPages = parseInt(this.pagination?.dataset.totalPages) || 1;
        this.apiBase = (window.wpApiSettings?.root || '/wp-json/') + 'venti/v1/members/';
        this.searchDebounce = null;

        this.init();
    }

    init() {
        if (this.form) {
            this.form.addEventListener('input', (e) => {
                const filter = e.target.dataset.filter;
                if (!filter) return;

                this.state[filter] = e.target.value;
                this.state.page = 1;

                if (filter === 'search') {
                    clearTimeout(this.searchDebounce);
                    this.searchDebounce = setTimeout(() => this.load(), 350);
                } else {
                    this.load();
                }
            });

            this.form.addEventListener('submit', (e) => e.preventDefault());
        }

        if (this.pagination) {
            this.pagination.querySelector('[data-page-prev]')?.addEventListener('click', () => {
                if (this.state.page > 1) {
                    this.state.page--;
                    this.load(true);
                }
            });

            this.pagination.querySelector('[data-page-next]')?.addEventListener('click', () => {
                if (this.state.page < this.totalPages) {
                    this.state.page++;
                    this.load(true);
                }
            });

            this.pagination.querySelector('[data-page-list]')?.addEventListener('click', (e) => {
                const btn = e.target.closest('[data-page]');
                if (!btn || btn.disabled) return;

                const page = parseInt(btn.dataset.page);
                if (page && page !== this.state.page) {
                    this.state.page = page;
                    this.load(true);
                }
            });
        }

        this.list.addEventListener('click', (e) => {
            const profileLink = e.target.closest('[data-member-profile]');
            if (!profileLink || window.wpApiSettings?.memberAuthAuthorized) return;

            e.preventDefault();
            this.openMemberAuth(profileLink.href);
        });

        this.renderPageNumbers();
    }

    openMemberAuth(link) {
        // Already has a valid (catalog-wide) session - go straight to the member
        // page instead of forcing the login modal again on every card click.
        if (window.wpApiSettings?.memberAuthAuthorized) {
            window.location.href = link;
            return;
        }

        document.dispatchEvent(new CustomEvent('venti:open-member-auth', { detail: { link } }));
    }

    buildColumnClass(columns) {
        const map = {
            2: 'col-12 col-md-6',
            3: 'col-12 col-md-6 col-xl-4',
        };
        return map[columns] || map[3];
    }

    buildUrl() {
        const params = new URLSearchParams({
            per_page: this.perPage,
            page: this.state.page,
        });

        if (this.state.search) params.set('search', this.state.search);
        if (this.state.sector) params.set('sector', this.state.sector);
        if (this.state.location) params.set('location', this.state.location);
        if (window.wpApiSettings?.lang) params.set('lang', window.wpApiSettings.lang);

        return this.apiBase + '?' + params.toString();
    }

    load(scrollToList = false) {
        if (scrollToList) {
            this.list.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }

        this.showLoader();

        fetch(this.buildUrl())
            .then((response) => response.json())
            .then((data) => {
                this.render(data.results || []);
                this.updatePagination(data.page || 1, data.total_pages || 1);
                this.hideLoader();
            })
            .catch(() => this.hideLoader());
    }

    render(members) {
        if (!members.length) {
            const col = document.createElement('div');
            col.className = 'col-12';
            const message = document.createElement('p');
            message.className = 'block-membership-directory__empty';
            message.textContent = this.emptyMessage;
            col.append(message);
            this.list.replaceChildren(col);
            return;
        }

        this.list.innerHTML = members.map((member) => this.renderCard(member)).join('');
    }

    renderCard(member) {
        const websiteHtml = member.website_url
            ? `<a href="${member.website_url}" target="_blank" rel="noopener" class="block-membership-directory__website">
                    website
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M12 8.66667V12.6667C12 13.0203 11.8595 13.3594 11.6095 13.6095C11.3594 13.8595 11.0203 14 10.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V5.33333C2 4.97971 2.14048 4.64057 2.39052 4.39052C2.64057 4.14048 2.97971 4 3.33333 4H7.33333" stroke="#CC2B27" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="fill: white !important;" fill="white"/>
                        <path d="M10 2H14V6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M6.66699 9.33333L14.0003 2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </a>`
            : '';

        // member.title/excerpt/sector come from WP core functions (get_the_title() etc.)
        // that already return HTML-ready strings (e.g. wptexturize turning "--" into the
        // entity for "-") - same trust boundary the Twig SSR template relies on via
        // Timber::$autoescape = false. Interpolated as-is here so "-" etc. render as
        // real characters instead of literal entity text; escHtml() is only for values
        // going into HTML attributes (alt/href), where it still applies.
        return `
            <div class="${this.columnClass} mb-6">
                <article class="block-membership-directory__card">
                    <div class="block-membership-directory__logo">
                        <div class="block-membership-directory__logo-inner">
                            <img src="${member.logo || this.noImage}" alt="${this.escHtml(member.title)}" loading="lazy"/>
                        </div>
                    </div>
                    <div class="block-membership-directory__content">
                        ${member.sector ? `<span class="block-membership-directory__sector">${member.sector}</span>` : ''}
                        <h3 class="block-membership-directory__title">
                            <a href="${this.escHtml(member.link)}" class="block-membership-directory__profile-link" data-member-profile>${member.title}</a>
                        </h3>
                        ${member.excerpt ? `<p class="block-membership-directory__excerpt">${member.excerpt}</p>` : ''}
                        ${websiteHtml}
                    </div>
                </article>
            </div>`;
    }

    updatePagination(page, totalPages) {
        if (!this.pagination) return;

        this.state.page = page;
        this.totalPages = totalPages;
        this.pagination.hidden = totalPages <= 1;
        this.pagination.dataset.currentPage = page;
        this.pagination.dataset.totalPages = totalPages;

        const prev = this.pagination.querySelector('[data-page-prev]');
        if (prev) prev.hidden = page <= 1;

        const next = this.pagination.querySelector('[data-page-next]');
        if (next) next.hidden = page >= totalPages;

        this.renderPageNumbers();
    }

    renderPageNumbers() {
        const list = this.pagination?.querySelector('[data-page-list]');
        if (!list) return;

        const current = this.state.page;
        const total = this.totalPages;

        list.innerHTML = this.buildPageRange(current, total)
            .map((item) => {
                if (item === '...') {
                    return `<li class="pagination__item dots"><span class="pagination__link">&hellip;</span></li>`;
                }

                const isCurrent = item === current;
                return `
                    <li class="pagination__item ${isCurrent ? 'pagination__item--current' : ''}">
                        <button type="button" class="pagination__link" data-page="${item}" ${isCurrent ? 'aria-current="page"' : ''}>${item}</button>
                    </li>`;
            })
            .join('');
    }

    buildPageRange(current, total) {
        if (total <= 7) {
            return Array.from({ length: total }, (_, i) => i + 1);
        }

        const pages = new Set([1, total, current, current - 1, current + 1]);
        const sorted = Array.from(pages)
            .filter((p) => p >= 1 && p <= total)
            .sort((a, b) => a - b);

        const result = [];
        sorted.forEach((page, index) => {
            if (index > 0 && page - sorted[index - 1] > 1) {
                result.push('...');
            }
            result.push(page);
        });

        return result;
    }

    showLoader() {
        this.list.style.opacity = '0.4';
        this.list.style.pointerEvents = 'none';
        if (this.loader) this.loader.style.display = 'flex';
    }

    hideLoader() {
        this.list.style.opacity = '1';
        this.list.style.pointerEvents = '';
        if (this.loader) this.loader.style.display = 'none';
    }

    escHtml(str) {
        const div = document.createElement('div');
        div.textContent = str || '';
        return div.innerHTML;
    }
}
